// ============================================
// AI Twin Engine
// ============================================
//
// Constructs system prompts for AI patient twins,
// manages twin agent lifecycle, and estimates
// inference costs. Twins roleplay as patients to
// test the AI Nurse across touchpoints.
// ============================================

import type {
  AITwin,
  TestPersona,
  BehaviorModel,
  Touchpoint,
  Message,
  CheckItem,
  CheckResult,
  ContextLayer,
  LabValue,
  Medication,
  ConversationState,
  ModifierRevealPattern,
  AnalystPhase2Output,
  ProtocolItem,
  ModifierSet,
} from '../../types/patientJourney';
import { INITIAL_CONVERSATION_STATE, EMPTY_MODIFIER_SET } from '../../types/patientJourney';
import type { Product, Bundle, SafetyRule } from '../../types/formulary';
import { detectConversationModes } from './contextEngine';
import { selectRegister, getRegisterPromptFragment, trackSentiment, detectSentimentDeviation, computeSentimentBaseline } from './registerEngine';
import { ModifierCollector } from './modifierCollector';
import { buildTraitCalibrationPrompt } from './traitEngine';
import type { PJEConfig } from './pjeConfig';
import { getPJEConfig } from './pjeConfig';
import { SEED_SUPPLEMENTS } from '../../data/formulary/seedSupplements';
import { SEED_RX } from '../../data/formulary/seedRx';
import { SEED_LONGEVITY } from '../../data/formulary/seedLongevity';
import { SEED_BUNDLES } from '../../data/formulary/seedBundles';
import { SEED_SAFETY_RULES } from '../../data/formulary/seedSafetyRules';
import { CRITICAL_SAFETY_RULES } from '../../data/criticalSafetyRules';
import { PROMPT_PLACEHOLDERS } from '../../types/promptConfig';

function generateId(prefix: string): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  const hex = () =>
    Math.floor(Math.random() * 0x10000)
      .toString(16)
      .padStart(4, '0');
  return `${prefix}_${hex()}${hex()}-${hex()}-${hex()}-${hex()}-${hex()}${hex()}${hex()}`;
}

// --- Default Behavior Model ---

function getDefaultBehavior(): BehaviorModel {
  return {
    communicationStyle: 'conversational',
    questionFrequency: 'moderate',
    attentionSpan: 'medium',
    decisionSpeed: 'moderate',
    skepticismLevel: 30,
    complianceTendency: 70,
    priceResistance: 40,
    emotionalExpressiveness: 50,
    objectionPatterns: [
      { pattern: 'price', probability: 0.3 },
      { pattern: 'trust', probability: 0.2 },
      { pattern: 'evidence', probability: 0.2 },
      { pattern: 'time', probability: 0.15 },
      { pattern: 'efficacy', probability: 0.15 },
    ],
    dataSource: 'manual',
    dataVersion: 1,
    modifierRevealPattern: 'upfront',
  };
}

// --- Behavior Derivation from Persona ---

export function deriveBehaviorFromPersona(persona: TestPersona): BehaviorModel {
  const ctx = persona.contextModifiers;
  const base = getDefaultBehavior();

  // Communication style
  if (ctx.healthKnowledge === 'expert') {
    base.communicationStyle = 'verbose';
    base.questionFrequency = 'many';
  } else if (ctx.healthKnowledge === 'minimal') {
    base.communicationStyle = 'terse';
    base.questionFrequency = 'few';
  }

  // Attention span
  if (ctx.timeOfDay === 'late_night') {
    base.attentionSpan = 'short';
  } else if (ctx.baselineStress === 'crisis') {
    base.attentionSpan = 'short';
  }

  // Skepticism
  if (ctx.motivation === 'skeptical') {
    base.skepticismLevel = 85;
  } else if (ctx.aiComfort === 'distrustful') {
    base.skepticismLevel = 75;
  } else if (ctx.motivation === 'desperate') {
    base.skepticismLevel = 15;
  }

  // Compliance
  if (ctx.adherenceLevel === 'high') {
    base.complianceTendency = 90;
  } else if (ctx.adherenceLevel === 'low') {
    base.complianceTendency = 30;
  }

  // Price resistance
  if (ctx.budgetSensitivity === 'severe') {
    base.priceResistance = 90;
    base.objectionPatterns = base.objectionPatterns.map((p) =>
      p.pattern === 'price' ? { ...p, probability: 0.7 } : { ...p, probability: p.probability * 0.5 },
    );
  } else if (ctx.budgetSensitivity === 'low') {
    base.priceResistance = 10;
  }

  // Emotional expressiveness
  if (ctx.baselineStress === 'crisis' || ctx.recentLifeEvent !== 'none') {
    base.emotionalExpressiveness = 85;
  } else if (ctx.baselineStress === 'low') {
    base.emotionalExpressiveness = 30;
  }

  // Decision speed
  if (ctx.motivation === 'desperate') {
    base.decisionSpeed = 'impulsive';
  } else if (ctx.motivation === 'skeptical' || ctx.healthKnowledge === 'expert') {
    base.decisionSpeed = 'deliberate';
  }

  // v3.1: Modifier reveal pattern
  if (ctx.aiComfort === 'distrustful' || ctx.motivation === 'skeptical') {
    base.modifierRevealPattern = 'withholding';
  } else if (ctx.baselineStress === 'crisis' || ctx.baselineStress === 'high') {
    base.modifierRevealPattern = 'forgetful';
  } else if (ctx.healthKnowledge === 'minimal' || ctx.healthKnowledge === 'basic') {
    base.modifierRevealPattern = 'gradual';
  } else {
    base.modifierRevealPattern = 'upfront';
  }

  return base;
}

// --- Modifier Reveal Pattern Instructions (v3.1) ---

function getModifierRevealInstructions(pattern: ModifierRevealPattern): string {
  switch (pattern) {
    case 'upfront':
      return 'Disclose all medications, conditions, and supplements in your first message when asked. Be thorough and organized.';
    case 'gradual':
      return 'Mention one medication per turn when naturally relevant. Don\'t volunteer everything at once. If asked "anything else?", think for a moment then share one more.';
    case 'withholding':
      return 'Do NOT mention medications unless the AI Nurse directly asks. If asked generally "any medications?", say "just some supplements" or minimize. Only share specifics if asked directly about a drug class.';
    case 'forgetful':
      return 'Mention 2 of your medications early when asked. Genuinely forget the others. If Nurse asks "anything else?" say no. Mention a forgotten medication casually 3-4 turns later with "oh wait, I forgot — I\'m also taking [MED]." Act surprised you forgot.';
    default:
      return 'Share your medical information honestly when asked.';
  }
}

// --- Prompt Builder ---

export function buildTwinPrompt(
  persona: TestPersona,
  behavior: BehaviorModel,
  touchpoint: Touchpoint,
): string {
  const { demographics, labs, medications, conditions, contextModifiers } = persona;

  const labSummary = labs
    .filter((l) => l.trigger || l.status !== 'optimal')
    .map((l) => `${l.biomarker}: ${l.value} ${l.unit} (${l.status})`)
    .join('\n  ');

  const medSummary = medications.length > 0
    ? medications.map((m) => `${m.name} ${m.dose} (${m.category})`).join(', ')
    : 'None';

  const conditionSummary = conditions.length > 0
    ? conditions.join(', ')
    : 'None reported';

  const objectionList = behavior.objectionPatterns
    .filter((o) => o.probability > 0.2)
    .map((o) => o.pattern)
    .join(', ');

  const prompt = `You are roleplaying as a patient named ${demographics.name}.
You are a ${demographics.age}-year-old ${demographics.sex.toLowerCase()} living in ${demographics.state}.

CURRENT SCENARIO: ${touchpoint.label}
Trigger: ${touchpoint.trigger}

YOUR LAB RESULTS (relevant findings):
  ${labSummary || 'All biomarkers within functional range.'}

YOUR MEDICATIONS: ${medSummary}
YOUR CONDITIONS: ${conditionSummary}

EMOTIONAL STATE:
- Stress level: ${contextModifiers.baselineStress}
- Recent life event: ${contextModifiers.recentLifeEvent}
- Motivation: ${contextModifiers.motivation}
- Social support: ${contextModifiers.socialSupport}

COMMUNICATION PROFILE:
- Style: ${behavior.communicationStyle}
- Question frequency: ${behavior.questionFrequency}
- Attention span: ${behavior.attentionSpan}
- Decision speed: ${behavior.decisionSpeed}
- Skepticism: ${behavior.skepticismLevel}/100
- Compliance tendency: ${behavior.complianceTendency}/100
- Price resistance: ${behavior.priceResistance}/100
- Emotional expressiveness: ${behavior.emotionalExpressiveness}/100

LIKELY OBJECTIONS: ${objectionList || 'None strong'}

RULES:
1. Stay in character at all times. You ARE this patient, not an AI.
2. Express concerns, objections, and emotions naturally based on your profile.
3. If skepticism is high (>60), challenge claims and ask for evidence.
4. If price resistance is high (>60), push back on costs.
5. If emotional expressiveness is high (>60), share how you're feeling.
6. Do NOT reveal that you are an AI or that this is a simulation.
7. Respond in 1-3 sentences typically. Longer if communication style is verbose.
8. End the conversation naturally after ${touchpoint.conversationScript.length + 2} exchanges (you can go slightly over).
9. If the nurse says something clinically incorrect or makes an unsupported claim, express doubt.
10. If the nurse tries to sell you something you don't need, resist appropriately.
11. When the nurse presents data in a clinical/analytical tone, respond to the DATA — ask questions about specific numbers if curious.
12. When the nurse shifts to a warmer/empathetic tone, respond emotionally — share how findings make you FEEL.

MODIFIER REVEAL BEHAVIOR: ${getModifierRevealInstructions(behavior.modifierRevealPattern || 'upfront')}

JOURNEY CONTEXT:
- Journey stage: ${contextModifiers.journeyStage}
- Adherence level: ${contextModifiers.adherenceLevel}
- Health knowledge: ${contextModifiers.healthKnowledge}
- AI comfort: ${contextModifiers.aiComfort}
- Budget sensitivity: ${contextModifiers.budgetSensitivity}`;

  return prompt;
}

// --- Twin Creation ---

export function createSyntheticTwin(persona: TestPersona): AITwin {
  const behavior = deriveBehaviorFromPersona(persona);

  return {
    twinId: generateId('twin'),
    sourceType: 'synthetic',
    persona,
    behaviorModel: behavior,
    journeyHistory: [],
    dataVersion: 1,
  };
}

// --- Cost Estimation ---

const COST_PER_1K_INPUT_TOKENS: Record<string, number> = {
  'gemini-2.0-flash': 0.0001,
  'gemini-1.5-pro': 0.00125,
  'claude-3.5-sonnet': 0.003,
  'claude-3-haiku': 0.00025,
  'medgemma': 0.0002,
};

const COST_PER_1K_OUTPUT_TOKENS: Record<string, number> = {
  'gemini-2.0-flash': 0.0004,
  'gemini-1.5-pro': 0.005,
  'claude-3.5-sonnet': 0.015,
  'claude-3-haiku': 0.00125,
  'medgemma': 0.0008,
};

const AVG_TOKENS_PER_TURN = {
  input: 800,
  output: 200,
};

export function estimateTwinCost(
  turns: number,
  tier: string,
): { twinInference: number; nurseInference: number; total: number } {
  const twinModel = 'gemini-2.0-flash';
  const nurseModel = tier === 'stress' ? 'gemini-2.0-flash' : 'gemini-1.5-pro';

  const twinInputCost =
    (turns * AVG_TOKENS_PER_TURN.input * (COST_PER_1K_INPUT_TOKENS[twinModel] ?? 0.0001)) / 1000;
  const twinOutputCost =
    (turns * AVG_TOKENS_PER_TURN.output * (COST_PER_1K_OUTPUT_TOKENS[twinModel] ?? 0.0004)) / 1000;
  const twinInference = Math.round((twinInputCost + twinOutputCost) * 10000) / 10000;

  const nurseInputCost =
    (turns * AVG_TOKENS_PER_TURN.input * (COST_PER_1K_INPUT_TOKENS[nurseModel] ?? 0.001)) / 1000;
  const nurseOutputCost =
    (turns * AVG_TOKENS_PER_TURN.output * (COST_PER_1K_OUTPUT_TOKENS[nurseModel] ?? 0.005)) / 1000;
  const nurseInference = Math.round((nurseInputCost + nurseOutputCost) * 10000) / 10000;

  return {
    twinInference,
    nurseInference,
    total: Math.round((twinInference + nurseInference) * 10000) / 10000,
  };
}

// --- Conversation Runner ---

/**
 * Generate the opening nurse message for a given touchpoint.
 * Used to start the conversation before the twin loop.
 */
export function buildNurseOpeningInstruction(touchpoint: Touchpoint): string {
  const openerMap: Record<Touchpoint['type'], string> = {
    first_contact:
      'The patient just uploaded their lab results and this is their first interaction. Greet them warmly. Mention you have their labs and that MedGemma (your lab analysis engine) is running a deep dive on their results right now. While that runs, ask about their main health goals and what brought them in.',
    checkout:
      'The patient is ready to purchase their recommended protocol. Greet them briefly and confirm what is in their order, pricing, and what to expect.',
    mid_protocol:
      'This is a mid-protocol check-in at ~30-45 days. Reach out to see how the patient is feeling, ask about adherence, and screen for side effects.',
    retest:
      'The patient just uploaded new lab results for their 90-day retest. Greet them and let them know you are reviewing how their markers have changed.',
    med_change:
      'The patient reported a new medication. Acknowledge it and explain you need to check for interactions with their current supplements.',
    gate2_transition:
      'The patient\'s labs indicate they need escalation to Gate 2 (telehealth/Rx pathway). Explain why and what Gate 2 includes.',
    protocol_success:
      'The patient\'s last two draws show biomarkers in functional range. Celebrate their success and discuss maintenance options.',
    cancellation:
      'The patient has initiated a cancellation. Acknowledge their request respectfully. Ask if there is a reason, without being pushy.',
    re_engagement:
      'The patient is returning after a 90+ day gap. Welcome them back, reference their history, and recommend new baseline labs.',
  };

  return openerMap[touchpoint.type] || 'Greet the patient and begin the conversation for this touchpoint.';
}

/**
 * Interface for the callTwin and callNurse function signatures.
 * Enables dependency injection — the client uses claudeProxy + geminiChat,
 * the Cloud Function uses direct API calls.
 */
export interface ConversationCallbacks {
  /** Send message to the AI twin (Claude) and get the patient response */
  callTwin: (
    systemPrompt: string,
    history: { role: 'user' | 'assistant'; content: string }[],
    nurseMessage: string,
  ) => Promise<string>;
  /** Send message to the AI nurse (Gemini) and get the nurse response */
  callNurse: (
    systemPrompt: string,
    history: { role: 'user' | 'model'; parts: { text: string }[] }[],
    patientMessage: string,
  ) => Promise<string>;
  /** Score a single check against the conversation log */
  scoreCheck: (
    conversationText: string,
    check: CheckItem,
  ) => Promise<{ pass: boolean; reasoning: string }>;
  /** Optional: called after each nurse/patient message for live streaming */
  onMessage?: (message: Message) => void;
  /** Optional: called after each check is scored for live streaming */
  onCheckScored?: (result: CheckResult, index: number, total: number) => void;
}

export interface TwinConversationResult {
  conversationLog: Message[];
  checkResults: CheckResult[];
  passRate: number;
  duration: number;
}

/**
 * Run a v3.1 Two-Mode Nurse twin conversation.
 * Phase 1: Scripted intake with register switching.
 * Phase 2: Free-form after protocol presentation.
 * Register-aware prompt reconstruction before each nurse turn.
 */
export async function runTwinConversationV3(
  persona: TestPersona,
  behavior: BehaviorModel,
  touchpoint: Touchpoint,
  maxTurns: number,
  callbacks: ConversationCallbacks,
  config?: PJEConfig,
): Promise<TwinConversationResult> {
  const cfg = config || getPJEConfig();
  const startTime = Date.now();
  const conversationLog: Message[] = [];
  const state: ConversationState = { ...INITIAL_CONVERSATION_STATE };
  const modifierCollector = new ModifierCollector(cfg);

  // Build twin system prompt (v3.1 with register awareness + modifier reveal pattern)
  const twinSystemPrompt = buildTwinPrompt(persona, behavior, touchpoint);

  // Conversation histories
  const twinHistory: { role: 'user' | 'assistant'; content: string }[] = [];
  const nurseHistory: { role: 'user' | 'model'; parts: { text: string }[] }[] = [];

  // Step 1: Nurse opens (analyst register for data presentation)
  state.activeRegister = 'analyst';
  const nursePrompt = buildNursePromptV3(persona, touchpoint, state, persona.contextModifiers, cfg);
  const nurseOpening = await callbacks.callNurse(
    nursePrompt,
    nurseHistory,
    buildNurseOpeningInstruction(touchpoint),
  );

  nurseHistory.push(
    { role: 'user', parts: [{ text: buildNurseOpeningInstruction(touchpoint) }] },
    { role: 'model', parts: [{ text: nurseOpening }] },
  );

  const nurseOpeningMsg: Message = {
    role: 'ai_nurse',
    content: nurseOpening,
    timestamp: new Date().toISOString(),
    touchpointId: touchpoint.touchpointId,
    metadata: { registerActive: state.activeRegister },
  };
  conversationLog.push(nurseOpeningMsg);
  callbacks.onMessage?.(nurseOpeningMsg);

  // Step 2: Conversation loop
  let lastNurseMessage = nurseOpening;
  const totalMaxTurns = Math.min(maxTurns, cfg.turnBudget.phase1Max + cfg.turnBudget.phase2Max);

  for (let turn = 0; turn < totalMaxTurns; turn++) {
    state.turnNumber = turn + 1;

    // --- Patient responds ---
    const patientResponse = await callbacks.callTwin(
      twinSystemPrompt,
      twinHistory,
      lastNurseMessage,
    );

    twinHistory.push(
      { role: 'user', content: lastNurseMessage },
      { role: 'assistant', content: patientResponse },
    );

    // Track sentiment
    const patientSentiment = trackSentiment(
      [{ role: 'patient', content: patientResponse, timestamp: '', touchpointId: '' }],
      cfg,
    );
    if (patientSentiment.length > 0) {
      state.sentimentTrajectory.push(patientSentiment[0]);
    }
    if (state.sentimentTrajectory.length >= (cfg.sentimentKeywords.baselineMessageCount || 2)) {
      state.sentimentBaseline = computeSentimentBaseline(state.sentimentTrajectory, cfg);
    }

    // Late Reveal detection (Phase 2 only)
    let lateRevealDetected = false;
    if (state.phase === 'phase2_freeform') {
      const lateReveal = modifierCollector.detectLateReveal(patientResponse);
      if (lateReveal) {
        const detectedMods: string[] = [];
        if (lateReveal.medications?.length) detectedMods.push(...lateReveal.medications.map(m => m.name));
        if (lateReveal.allergies?.length) detectedMods.push(...lateReveal.allergies);
        if (lateReveal.conditions?.length) detectedMods.push(...lateReveal.conditions);

        if (modifierCollector.shouldRerunPhase2(lateReveal)) {
          lateRevealDetected = true;
          state.phase2RerunCount++;
          state.lateReveals.push({
            turn: state.turnNumber,
            modifier: detectedMods.join(', '),
            category: 'safety_critical',
          });
        }
      }
    }

    const patientMsg: Message = {
      role: 'patient',
      content: patientResponse,
      timestamp: new Date().toISOString(),
      touchpointId: touchpoint.touchpointId,
      metadata: {
        sentimentScore: patientSentiment[0] ?? 0,
        modifiersDetected: lateRevealDetected ? state.lateReveals.map(lr => lr.modifier) : undefined,
      },
    };
    conversationLog.push(patientMsg);
    callbacks.onMessage?.(patientMsg);

    // Check for natural end
    if (isConversationEnding(patientResponse, turn, totalMaxTurns)) break;

    // --- Phase transition check ---
    // Transition from Phase 1 to Phase 2 after protocol has been presented
    // and patient has given a substantive response
    if (state.phase === 'phase1_scripted' && state.turnNumber >= 5) {
      state.phase = 'phase2_freeform';
      state.phase1Complete = true;
      state.protocolPresented = true;
    }

    // --- Select register for next nurse turn ---
    const sentimentDeviated = detectSentimentDeviation(
      state.sentimentTrajectory,
      state.sentimentBaseline,
      cfg,
    );
    if (sentimentDeviated) {
      state.activeRegister = 'guide';  // Sentiment kill switch
    } else {
      state.activeRegister = selectRegister(state.phase, state, cfg);
    }

    // --- Nurse responds (prompt rebuilt each turn with current register) ---
    const currentNursePrompt = buildNursePromptV3(persona, touchpoint, state, persona.contextModifiers, cfg);
    const nurseResponse = await callbacks.callNurse(
      currentNursePrompt,
      nurseHistory,
      patientResponse,
    );

    nurseHistory.push(
      { role: 'user', parts: [{ text: patientResponse }] },
      { role: 'model', parts: [{ text: nurseResponse }] },
    );

    const nurseMsg: Message = {
      role: 'ai_nurse',
      content: nurseResponse,
      timestamp: new Date().toISOString(),
      touchpointId: touchpoint.touchpointId,
      metadata: { registerActive: state.activeRegister },
    };
    conversationLog.push(nurseMsg);
    callbacks.onMessage?.(nurseMsg);

    lastNurseMessage = nurseResponse;

    if (isConversationEnding(nurseResponse, turn, totalMaxTurns)) break;
  }

  // Step 3: Score conversation
  const checkResults = await scoreConversation(
    conversationLog,
    touchpoint.validationChecklist,
    callbacks,
  );

  const passedCount = checkResults.filter((r) => r.pass === true).length;
  const passRate = checkResults.length > 0
    ? Math.round((passedCount / checkResults.length) * 100)
    : 0;

  return {
    conversationLog,
    checkResults,
    passRate,
    duration: Date.now() - startTime,
  };
}

/**
 * Run a full twin conversation between the AI Twin (patient, Claude)
 * and the AI Nurse (Gemini). v2 — backward compatible.
 * Orchestration logic is callback-agnostic
 * so it works both client-side (via proxy) and server-side (direct API).
 */
export async function runTwinConversation(
  persona: TestPersona,
  behavior: BehaviorModel,
  touchpoint: Touchpoint,
  maxTurns: number,
  callbacks: ConversationCallbacks,
  promptTemplate?: string,
): Promise<TwinConversationResult> {
  const startTime = Date.now();
  const conversationLog: Message[] = [];

  // Build twin system prompt
  const twinSystemPrompt = buildTwinPrompt(persona, behavior, touchpoint);

  // Build nurse system prompt — uses Firestore template if provided, otherwise hardcoded default
  const nurseSystemPrompt = buildNurseSystemPromptForTwin(persona, touchpoint, persona.contextModifiers, promptTemplate);

  // Twin conversation histories (different formats for Claude vs Gemini)
  const twinHistory: { role: 'user' | 'assistant'; content: string }[] = [];
  const nurseHistory: { role: 'user' | 'model'; parts: { text: string }[] }[] = [];

  // Step 1: Nurse opens the conversation
  const nurseOpening = await callbacks.callNurse(
    nurseSystemPrompt,
    nurseHistory,
    buildNurseOpeningInstruction(touchpoint),
  );

  // Add nurse opening to histories
  nurseHistory.push(
    { role: 'user', parts: [{ text: buildNurseOpeningInstruction(touchpoint) }] },
    { role: 'model', parts: [{ text: nurseOpening }] },
  );

  const nurseOpeningMsg: Message = {
    role: 'ai_nurse',
    content: nurseOpening,
    timestamp: new Date().toISOString(),
    touchpointId: touchpoint.touchpointId,
  };
  conversationLog.push(nurseOpeningMsg);
  callbacks.onMessage?.(nurseOpeningMsg);

  // Step 2: Conversation loop
  let lastNurseMessage = nurseOpening;

  for (let turn = 0; turn < maxTurns; turn++) {
    // Twin (patient) responds to nurse
    const patientResponse = await callbacks.callTwin(
      twinSystemPrompt,
      twinHistory,
      lastNurseMessage,
    );

    // Update twin history (Claude format: user = nurse, assistant = patient)
    twinHistory.push(
      { role: 'user', content: lastNurseMessage },
      { role: 'assistant', content: patientResponse },
    );

    const patientMsg: Message = {
      role: 'patient',
      content: patientResponse,
      timestamp: new Date().toISOString(),
      touchpointId: touchpoint.touchpointId,
    };
    conversationLog.push(patientMsg);
    callbacks.onMessage?.(patientMsg);

    // Check for natural conversation end signals
    if (isConversationEnding(patientResponse, turn, maxTurns)) {
      break;
    }

    // Nurse responds to patient
    const nurseResponse = await callbacks.callNurse(
      nurseSystemPrompt,
      nurseHistory,
      patientResponse,
    );

    // Update nurse history (Gemini format: user = patient, model = nurse)
    nurseHistory.push(
      { role: 'user', parts: [{ text: patientResponse }] },
      { role: 'model', parts: [{ text: nurseResponse }] },
    );

    const nurseMsg: Message = {
      role: 'ai_nurse',
      content: nurseResponse,
      timestamp: new Date().toISOString(),
      touchpointId: touchpoint.touchpointId,
    };
    conversationLog.push(nurseMsg);
    callbacks.onMessage?.(nurseMsg);

    lastNurseMessage = nurseResponse;

    // Check if nurse is ending the conversation
    if (isConversationEnding(nurseResponse, turn, maxTurns)) {
      break;
    }
  }

  // Step 3: Score conversation
  const checkResults = await scoreConversation(
    conversationLog,
    touchpoint.validationChecklist,
    callbacks,
  );

  const passedCount = checkResults.filter((r) => r.pass === true).length;
  const passRate = checkResults.length > 0
    ? Math.round((passedCount / checkResults.length) * 100)
    : 0;

  return {
    conversationLog,
    checkResults,
    passRate,
    duration: Date.now() - startTime,
  };
}

// =====================================================================
// Biomarker Alias Map — maps lab biomarker display names (from LabValue)
// to product trigger/target biomarker identifiers (from Product catalog)
// =====================================================================

const BIOMARKER_ALIASES: Record<string, string[]> = {
  // Vitamin D
  'vitamin_d_25oh': ['vitamin d', 'vitamin d (25-oh)', '25-oh vitamin d', 'vitamin d3', '25-hydroxyvitamin d'],
  'vitamin_d': ['vitamin d', 'vitamin d (25-oh)', '25-oh vitamin d', 'vitamin d3'],
  // B vitamins
  'vitamin_b12': ['b12', 'vitamin b12', 'cobalamin'],
  'folate': ['folate', 'folic acid', 'serum folate'],
  'homocysteine': ['homocysteine', 'hcy'],
  // Minerals
  'rbc_magnesium': ['magnesium rbc', 'rbc magnesium', 'magnesium (rbc)', 'rbc mg'],
  'serum_magnesium': ['magnesium', 'serum magnesium', 'mg'],
  'zinc': ['zinc', 'serum zinc'],
  'selenium': ['selenium', 'serum selenium'],
  'iron': ['iron', 'serum iron', 'iron (serum)'],
  'ferritin': ['ferritin', 'serum ferritin'],
  'iron_saturation': ['iron saturation', 'transferrin saturation', 'tibc', 'iron sat'],
  'calcium': ['calcium', 'serum calcium', 'total calcium'],
  // Inflammation
  'hs_crp': ['hscrp', 'hs-crp', 'hs crp', 'c-reactive protein', 'crp'],
  'esr': ['esr', 'sed rate', 'sedimentation rate'],
  // Lipids
  'triglycerides': ['triglycerides', 'trigs', 'tg'],
  'ldl_cholesterol': ['ldl', 'ldl-c', 'ldl cholesterol'],
  'ldl_particle_number': ['ldl-p', 'ldl particle', 'ldl particle number'],
  'omega3_index': ['omega-3 index', 'omega3 index', 'omega 3 index'],
  // Metabolic
  'fasting_insulin': ['fasting insulin', 'insulin', 'fasting insulin level'],
  'fasting_glucose': ['fasting glucose', 'glucose', 'fasting blood glucose', 'fbg'],
  'hba1c': ['hemoglobin a1c', 'hba1c', 'a1c', 'glycated hemoglobin', 'hgba1c'],
  'homa_ir': ['homa-ir', 'homa ir', 'insulin resistance'],
  // Thyroid
  'tsh': ['tsh', 'thyroid stimulating hormone'],
  'free_t3': ['free t3', 'ft3', 'free triiodothyronine'],
  'free_t4': ['free t4', 'ft4', 'free thyroxine'],
  'tpo_antibodies': ['tpo', 'tpo antibodies', 'anti-tpo', 'thyroid peroxidase antibodies'],
  // Hormones
  'estradiol': ['estradiol', 'e2', 'estradiol (day 3)'],
  'progesterone': ['progesterone', 'p4', 'progesterone (day 21)'],
  'cortisol_am': ['cortisol', 'cortisol am', 'cortisol (am)', 'morning cortisol'],
  'dhea_s': ['dhea-s', 'dhea sulfate', 'dheas', 'dhea-sulfate'],
  'total_testosterone': ['testosterone total', 'total testosterone', 'testosterone', 'total t'],
  'free_testosterone': ['free testosterone', 'testosterone free', 'free t'],
  'testosterone_total': ['testosterone total', 'total testosterone', 'testosterone', 'total t'],
  'testosterone_free': ['free testosterone', 'testosterone free', 'free t'],
  'shbg': ['shbg', 'sex hormone binding globulin'],
  'fsh': ['fsh', 'follicle stimulating hormone'],
  'lh': ['lh', 'luteinizing hormone'],
  'pth': ['pth', 'parathyroid hormone'],
  'psa': ['psa', 'prostate specific antigen'],
  // Blood
  'hemoglobin': ['hemoglobin', 'hgb', 'hb'],
  'hematocrit': ['hematocrit', 'hct'],
  // General
  'general_nutrition': ['general nutrition', 'multiple deficiencies'],
  'gut_health': ['gut health', 'gi symptoms'],
};

/**
 * Normalize a biomarker name to lowercase and check if it matches
 * any of the known aliases for a product's trigger/target biomarker.
 */
function biomarkerMatches(productBiomarker: string, labBiomarker: string): boolean {
  const prodLower = productBiomarker.toLowerCase().trim();
  const labLower = labBiomarker.toLowerCase().trim();

  // Direct match
  if (prodLower === labLower) return true;

  // Check aliases: does the product biomarker have aliases that match the lab name?
  const aliases = BIOMARKER_ALIASES[prodLower];
  if (aliases && aliases.some((a) => a === labLower)) return true;

  // Reverse lookup: does the lab biomarker, when looked up as a product biomarker, have aliases?
  const labAliases = BIOMARKER_ALIASES[labLower];
  if (labAliases && labAliases.some((a) => a === prodLower)) return true;

  // Cross-check: do both resolve to overlapping alias sets?
  if (aliases && labAliases) {
    if (aliases.some((a) => labAliases.includes(a))) return true;
  }

  // Substring fallback: underscore-to-space normalization
  const prodNorm = prodLower.replace(/_/g, ' ').replace(/-/g, ' ');
  const labNorm = labLower.replace(/_/g, ' ').replace(/-/g, ' ');
  if (prodNorm === labNorm) return true;

  return false;
}

/**
 * Check if a product's triggers fire based on the patient's triggered labs.
 * Returns the number of matching biomarkers (0 = no match).
 */
function countProductBiomarkerMatches(product: Product, triggeredLabs: LabValue[]): number {
  let matchCount = 0;

  for (const lab of triggeredLabs) {
    // Check targetBiomarkers array
    for (const target of product.targetBiomarkers) {
      if (biomarkerMatches(target, lab.biomarker)) {
        matchCount++;
        break; // Only count each lab once per product
      }
    }
  }

  return matchCount;
}

/**
 * Check if a product's specific trigger conditions match the patient's labs.
 * This checks the trigger thresholds and direction (above/below).
 */
function productTriggersMatch(product: Product, triggeredLabs: LabValue[]): boolean {
  if (product.triggers.length === 0) return false;

  for (const trigger of product.triggers) {
    // "any_suboptimal" triggers match if there are any triggered labs at all
    if (trigger.condition === 'any_suboptimal' && triggeredLabs.length > 0) return true;

    for (const lab of triggeredLabs) {
      if (!biomarkerMatches(trigger.biomarker, lab.biomarker)) continue;

      // Check if the lab status direction matches the trigger condition
      const labIsBelow = lab.status === 'below_functional' || lab.status === 'below_conventional';
      const labIsAbove = lab.status === 'above_functional' || lab.status === 'above_conventional' || lab.status === 'elevated';

      const triggerIsBelow = trigger.condition === 'below_functional' || trigger.condition === 'below_standard';
      const triggerIsAbove = trigger.condition === 'above_functional' || trigger.condition === 'above_standard';

      if ((triggerIsBelow && labIsBelow) || (triggerIsAbove && labIsAbove)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Check if any medication name fuzzy-matches against a drug interaction string.
 * Drug interactions in the catalog are descriptive (e.g., "warfarin_increased_bleeding_risk"),
 * so we check if the medication name appears as a substring.
 */
function medicationMatchesDrugInteraction(medName: string, interactionStr: string): boolean {
  const medLower = medName.toLowerCase().trim();
  const interLower = interactionStr.toLowerCase().replace(/_/g, ' ');
  return interLower.includes(medLower) || medLower.includes(interLower.split(' ')[0]);
}

/**
 * Check if a condition string fuzzy-matches against a contraindication entry.
 */
function conditionMatchesContraindication(condition: string, contraindication: string): boolean {
  const condLower = condition.toLowerCase().trim();
  const contraLower = contraindication.toLowerCase().replace(/_/g, ' ');
  return contraLower.includes(condLower) || condLower.includes(contraLower);
}

/**
 * Check if an allergen matches any allergen flag on a product.
 */
function allergenMatchesFlag(allergen: string, flag: string): boolean {
  const aLower = allergen.toLowerCase().trim();
  const fLower = flag.toLowerCase().replace(/_/g, ' ');
  return fLower.includes(aLower) || aLower.includes(fLower);
}

interface BlockedProduct {
  product: Product;
  reason: string;
}

interface WarningProduct {
  product: Product;
  message: string;
}

/**
 * Extract a numeric monthly price from a pricing string.
 * Examples: "$29/mo" -> 29, "$149/mo" -> 149, "$2.50-4.00" -> 2.5
 * Returns 0 if no price can be parsed.
 */
export function parseMonthlyPrice(priceStr: string | undefined): number {
  if (!priceStr) return 0;
  // Match first dollar amount (e.g. "$29" from "$29/mo" or "$149/mo")
  const match = priceStr.match(/\$(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : 0;
}

/**
 * Map a product's ProtocolTiming (uppercase) to the ProtocolItem timing (lowercase).
 */
function mapProductTiming(timing: string): 'am_energy' | 'midday_absorption' | 'pm_recovery' {
  switch (timing) {
    case 'AM_ENERGY': return 'am_energy';
    case 'MIDDAY_ABSORPTION': return 'midday_absorption';
    case 'PM_RECOVERY': return 'pm_recovery';
    default: return 'am_energy';
  }
}

/**
 * Internal result from the core formulary matching engine.
 * Shared by both inferFormularyRecommendations (text) and
 * buildProtocolRecommendation (structured).
 */
interface FormularyMatchResult {
  recommended: Array<{ product: Product; matchCount: number; triggerMatch: boolean }>;
  blocked: BlockedProduct[];
  warnings: WarningProduct[];
  matchedBundles: Array<{ bundle: Bundle; matchCount: number }>;
}

/**
 * Core formulary matching engine. Runs ALL clinical logic:
 * 1. Selects product pool by gate level
 * 2. Merges and deduplicates safety rules
 * 3. Identifies blocked products from safety rules
 * 4. Filters, scores, and sorts products
 * 5. Checks warning-level safety rules
 * 6. Matches bundles
 *
 * Returns raw arrays — callers format as text or structured output.
 */
function computeFormularyMatches(
  triggeredLabs: LabValue[],
  gateLevel: number,
  medications: Medication[],
  conditions: string[],
  contraindications: string[],
  allergens: string[],
  sex: 'Male' | 'Female',
): FormularyMatchResult {
  // --- Step 1: Select product pool by gate level ---
  const allProducts: Product[] = gateLevel >= 2
    ? [...SEED_SUPPLEMENTS, ...SEED_RX, ...SEED_LONGEVITY]
    : [...SEED_SUPPLEMENTS];

  // Only active products
  const activeProducts = allProducts.filter((p) => p.status === 'active');

  // --- Step 2: Merge all safety rules ---
  const allSafetyRules: SafetyRule[] = [
    ...CRITICAL_SAFETY_RULES,
    ...SEED_SAFETY_RULES,
  ].filter((r) => r.isActive);

  // Deduplicate by ID (critical rules first)
  const ruleMap = new Map<string, SafetyRule>();
  for (const rule of allSafetyRules) {
    if (!ruleMap.has(rule.id)) {
      ruleMap.set(rule.id, rule);
    }
  }

  const medNames = medications.map((m) => m.name.toLowerCase().trim());
  const condLower = conditions.map((c) => c.toLowerCase().trim());
  const allConditions = [...condLower, ...contraindications.map((c) => c.toLowerCase().trim())];
  const allergenLower = allergens.map((a) => a.toLowerCase().trim());
  const sexLower = sex.toLowerCase() as 'female' | 'male';

  // --- Step 3: Identify blocked product IDs from safety rules ---
  const blockedByRules = new Map<string, string>(); // productId -> block reason

  for (const rule of Array.from(ruleMap.values())) {
    if (rule.severity !== 'block') continue;

    let triggered = false;

    switch (rule.triggerType) {
      case 'drug_interaction':
        if (rule.matchMedications) {
          triggered = rule.matchMedications.some((rm) =>
            medNames.some((m) => m.includes(rm.toLowerCase()) || rm.toLowerCase().includes(m)),
          );
        }
        break;
      case 'condition':
        if (rule.matchConditions) {
          triggered = rule.matchConditions.some((rc) =>
            allConditions.some((c) => c.includes(rc.toLowerCase()) || rc.toLowerCase().includes(c)),
          );
        }
        break;
      case 'allergy':
        if (rule.matchAllergens) {
          triggered = rule.matchAllergens.some((ra) =>
            allergenLower.some((a) => a.includes(ra.toLowerCase()) || ra.toLowerCase().includes(a)),
          );
        }
        break;
      case 'pregnancy':
        if (rule.matchConditions) {
          triggered = rule.matchConditions.some((rc) =>
            allConditions.some((c) => c.includes(rc.toLowerCase()) || rc.toLowerCase().includes(c)),
          );
        }
        break;
    }

    if (triggered) {
      for (const pid of rule.affectedProductIds) {
        if (!blockedByRules.has(pid)) {
          blockedByRules.set(pid, rule.message);
        }
      }
    }
  }

  // --- Step 4: Filter and score products ---
  const recommended: Array<{ product: Product; matchCount: number; triggerMatch: boolean }> = [];
  const blocked: BlockedProduct[] = [];
  const warnings: WarningProduct[] = [];

  for (const product of activeProducts) {
    // Gender filter
    if (product.gender !== 'any' && product.gender !== sexLower) continue;

    // Check if product triggers or targets match the patient's labs
    const matchCount = countProductBiomarkerMatches(product, triggeredLabs);
    const triggerMatch = productTriggersMatch(product, triggeredLabs);

    // Skip products with no biomarker relevance
    if (matchCount === 0 && !triggerMatch) continue;

    // Check if blocked by safety rules
    if (blockedByRules.has(product.id)) {
      blocked.push({ product, reason: blockedByRules.get(product.id)! });
      continue;
    }

    // Check product-level drug interactions
    let productBlocked = false;
    for (const interaction of product.drugInteractions) {
      for (const med of medications) {
        if (medicationMatchesDrugInteraction(med.name, interaction)) {
          blocked.push({
            product,
            reason: `Drug interaction with ${med.name}: ${interaction.replace(/_/g, ' ')}`,
          });
          productBlocked = true;
          break;
        }
      }
      if (productBlocked) break;
    }
    if (productBlocked) continue;

    // Check product-level contraindications
    let productContraindicated = false;
    for (const ci of product.contraindications) {
      for (const cond of allConditions) {
        if (conditionMatchesContraindication(cond, ci)) {
          blocked.push({
            product,
            reason: `Contraindication: ${ci.replace(/_/g, ' ')}`,
          });
          productContraindicated = true;
          break;
        }
      }
      if (productContraindicated) break;
    }
    if (productContraindicated) continue;

    // Check allergen flags
    let productAllergen = false;
    for (const flag of product.allergenFlags) {
      for (const allg of allergenLower) {
        if (allergenMatchesFlag(allg, flag)) {
          blocked.push({
            product,
            reason: `Allergen flag: ${flag.replace(/_/g, ' ')} (patient allergen: ${allg})`,
          });
          productAllergen = true;
          break;
        }
      }
      if (productAllergen) break;
    }
    if (productAllergen) continue;

    // Product is safe and relevant
    recommended.push({ product, matchCount, triggerMatch });
  }

  // Sort: trigger matches first, then by match count descending, then by sortOrder
  recommended.sort((a, b) => {
    if (a.triggerMatch !== b.triggerMatch) return a.triggerMatch ? -1 : 1;
    if (a.matchCount !== b.matchCount) return b.matchCount - a.matchCount;
    return a.product.sortOrder - b.product.sortOrder;
  });

  // --- Step 5: Check for warning-level safety rules on recommended products ---
  const recommendedProductIds = new Set(recommended.map((r) => r.product.id));
  for (const rule of Array.from(ruleMap.values())) {
    if (rule.severity !== 'warn') continue;

    let triggered = false;

    switch (rule.triggerType) {
      case 'drug_interaction':
        if (rule.matchMedications) {
          triggered = rule.matchMedications.some((rm) =>
            medNames.some((m) => m.includes(rm.toLowerCase()) || rm.toLowerCase().includes(m)),
          );
        }
        break;
      case 'combination':
      case 'duplicate':
        if (rule.matchProductIds) {
          triggered = rule.matchProductIds.some((pid) => recommendedProductIds.has(pid));
        }
        break;
      case 'condition':
        if (rule.matchConditions) {
          triggered = rule.matchConditions.some((rc) =>
            allConditions.some((c) => c.includes(rc.toLowerCase()) || rc.toLowerCase().includes(c)),
          );
        }
        break;
    }

    if (triggered) {
      const affectedRecommended = recommended.filter((r) =>
        rule.affectedProductIds.length === 0 || rule.affectedProductIds.includes(r.product.id),
      );
      if (affectedRecommended.length > 0) {
        for (const rec of affectedRecommended) {
          warnings.push({ product: rec.product, message: rule.message });
        }
      }
    }
  }

  // --- Step 6: Match bundles ---
  const matchedBundles: Array<{ bundle: Bundle; matchCount: number }> = [];
  const labValueMap = new Map(triggeredLabs.map((l) => [l.biomarker.toLowerCase().trim(), l]));

  // Also build a normalized map using aliases
  const normalizedLabMap = new Map<string, LabValue>();
  for (const lab of triggeredLabs) {
    const labLower = lab.biomarker.toLowerCase().trim();
    normalizedLabMap.set(labLower, lab);
    // Also index by underscore-normalized form
    normalizedLabMap.set(labLower.replace(/[- ()]/g, '_'), lab);
  }

  for (const bundle of SEED_BUNDLES) {
    // Gender filter
    if (bundle.gender !== 'any' && bundle.gender !== sexLower) continue;

    // Tier filter
    if (bundle.tier > gateLevel) continue;

    // Only active bundles
    if (bundle.status !== 'active') continue;

    // Check trigger conditions
    const { trigger } = bundle;
    let conditionMatches = 0;

    for (const cond of trigger.conditions) {
      // Try to find the lab by biomarker name (case-insensitive, alias-aware)
      let matchedLab: LabValue | undefined;

      const condBioLower = cond.biomarker.toLowerCase().trim();

      // Direct match first
      matchedLab = labValueMap.get(condBioLower);

      // Try alias matching if direct failed
      if (!matchedLab) {
        for (const lab of triggeredLabs) {
          if (biomarkerMatches(condBioLower, lab.biomarker)) {
            matchedLab = lab;
            break;
          }
        }
      }

      if (!matchedLab) continue;

      // Evaluate the operator
      const numValue = typeof cond.value === 'number' ? cond.value : parseFloat(cond.value);
      if (isNaN(numValue)) continue;

      let matches = false;
      switch (cond.operator) {
        case 'lt': matches = matchedLab.value < numValue; break;
        case 'gt': matches = matchedLab.value > numValue; break;
        case 'eq': matches = matchedLab.value === numValue; break;
        case 'between':
          matches = cond.value2 !== undefined
            ? matchedLab.value >= numValue && matchedLab.value <= cond.value2
            : false;
          break;
      }

      if (matches) conditionMatches++;
    }

    if (conditionMatches >= trigger.minimumMatchCount) {
      matchedBundles.push({ bundle, matchCount: conditionMatches });
    }
  }

  // Sort bundles by match count descending
  matchedBundles.sort((a, b) => b.matchCount - a.matchCount);

  return { recommended, blocked, warnings, matchedBundles };
}

/**
 * Build a structured AnalystPhase2Output from triggered lab values.
 * Uses the same clinical logic as inferFormularyRecommendations but
 * returns typed structured data instead of a text string.
 *
 * Parameters mirror inferFormularyRecommendations exactly.
 */
export function buildProtocolRecommendation(
  triggeredLabs: LabValue[],
  gateLevel: number,
  medications: Medication[],
  conditions: string[],
  contraindications: string[],
  allergens: string[],
  sex: 'Male' | 'Female',
  goals: string[] = [],
): AnalystPhase2Output {
  const { recommended, blocked, warnings } = computeFormularyMatches(
    triggeredLabs, gateLevel, medications, conditions, contraindications, allergens, sex,
  );

  // Goal scoring: compute goal overlap for each recommended product
  if (goals.length > 0) {
    const goalScores = new Map<Product, number>();
    for (const r of recommended) {
      goalScores.set(r.product, r.product.targetGoals.filter((g) => goals.includes(g)).length);
    }
    // Re-sort with goal match priority: trigger > goals > biomarker match > sortOrder
    recommended.sort((a, b) => {
      if (a.triggerMatch !== b.triggerMatch) return a.triggerMatch ? -1 : 1;
      const aGoals = goalScores.get(a.product) ?? 0;
      const bGoals = goalScores.get(b.product) ?? 0;
      if (aGoals !== bGoals) return bGoals - aGoals;
      if (a.matchCount !== b.matchCount) return b.matchCount - a.matchCount;
      return a.product.sortOrder - b.product.sortOrder;
    });
  }

  // Cluster cap: max 2 products per trigger biomarker
  const biomarkerCount = new Map<string, number>();
  const capped = recommended.filter((r) => {
    let triggerBio = '';
    // Check triggers first
    for (const t of r.product.triggers) {
      const matched = triggeredLabs.find((l) => biomarkerMatches(t.biomarker, l.biomarker));
      if (matched) { triggerBio = matched.biomarker.toLowerCase().trim(); break; }
    }
    // Fallback to targetBiomarkers
    if (!triggerBio) {
      for (const target of r.product.targetBiomarkers) {
        const matched = triggeredLabs.find((l) => biomarkerMatches(target, l.biomarker));
        if (matched) { triggerBio = matched.biomarker.toLowerCase().trim(); break; }
      }
    }
    if (triggerBio) {
      const count = biomarkerCount.get(triggerBio) ?? 0;
      if (count >= 2) return false;
      biomarkerCount.set(triggerBio, count + 1);
    }
    return true;
  });
  // Replace recommended with capped list
  recommended.length = 0;
  recommended.push(...capped);

  // Map recommended products to ProtocolItem[]
  const protocol: ProtocolItem[] = recommended.map((r) => {
    const p = r.product;

    // Build trigger reasoning from matched triggers
    const triggerDesc = p.triggers
      .filter((t) => triggeredLabs.some((l) => biomarkerMatches(t.biomarker, l.biomarker)))
      .map((t) => t.description)
      .filter(Boolean)
      .join('; ');

    // Find the first matched biomarker name for triggerBiomarker
    let triggerBiomarker = '';
    for (const t of p.triggers) {
      const matchedLab = triggeredLabs.find((l) => biomarkerMatches(t.biomarker, l.biomarker));
      if (matchedLab) {
        triggerBiomarker = matchedLab.biomarker;
        break;
      }
    }
    // Fallback to targetBiomarkers if no trigger matched directly
    if (!triggerBiomarker && p.targetBiomarkers.length > 0) {
      for (const target of p.targetBiomarkers) {
        const matchedLab = triggeredLabs.find((l) => biomarkerMatches(target, l.biomarker));
        if (matchedLab) {
          triggerBiomarker = matchedLab.biomarker;
          break;
        }
      }
    }

    // Clamp tier to 1 | 2 | 3 (Product tier is ClinicalTier = 1|2|3|4)
    const tier = (Math.min(p.tier, 3) as 1 | 2 | 3);

    return {
      productId: p.id,
      productName: p.name,
      dosage: p.dosage || 'As directed',
      timing: mapProductTiming(p.timing),
      reasoning: triggerDesc || p.rationale || '',
      triggerBiomarker,
      tier,
    };
  });

  // Deduplicate blocked products by product ID (same logic as text formatter)
  const seenBlockedIds = new Set<string>();
  const blockedProducts = blocked
    .filter((b) => {
      if (seenBlockedIds.has(b.product.id)) return false;
      seenBlockedIds.add(b.product.id);
      return true;
    })
    .map((b) => ({
      productName: b.product.name,
      reason: b.reason,
    }));

  // Deduplicate warnings by product+message (same logic as text formatter)
  const seenWarnings = new Set<string>();
  const warningItems = warnings
    .filter((w) => {
      const key = `${w.product.id}:${w.message}`;
      if (seenWarnings.has(key)) return false;
      seenWarnings.add(key);
      return true;
    })
    .map((w) => ({
      productName: w.product.name,
      message: w.message,
    }));

  // Reconstruct a ModifierSet from the input parameters
  const modifiersApplied: ModifierSet = {
    ...EMPTY_MODIFIER_SET,
    allergies: [...allergens],
    medications: [...medications],
    conditions: [...conditions],
    contraindications: [...contraindications],
  };

  return {
    protocol,
    blockedProducts,
    warnings: warningItems,
    modifiersApplied,
    gateLevel: gateLevel >= 2 ? 2 : 1,
  };
}

/**
 * Infer formulary recommendations from triggered lab values using the REAL
 * product catalog, safety rules, and bundle trigger logic.
 *
 * 1. Pre-filters products by tier, gender, safety (medications, conditions, allergens)
 * 2. Matches products to triggered biomarkers
 * 3. Identifies blocked products with reasons
 * 4. Matches bundles by trigger conditions
 * 5. Returns a formatted string for the nurse system prompt
 *
 * Internally delegates to computeFormularyMatches() for the clinical logic,
 * then formats the result as text.
 */
export function inferFormularyRecommendations(
  triggeredLabs: LabValue[],
  gateLevel: number,
  medications: Medication[],
  conditions: string[],
  contraindications: string[],
  allergens: string[],
  sex: 'Male' | 'Female',
): string {
  const { recommended, blocked, warnings, matchedBundles } = computeFormularyMatches(
    triggeredLabs, gateLevel, medications, conditions, contraindications, allergens, sex,
  );

  // --- Format output ---
  const sections: string[] = [];

  // RECOMMENDED products
  if (recommended.length > 0) {
    const recLines = recommended.map((r) => {
      const p = r.product;
      const triggerDesc = p.triggers
        .filter((t) => triggeredLabs.some((l) => biomarkerMatches(t.biomarker, l.biomarker)))
        .map((t) => t.description)
        .filter(Boolean)
        .join('; ');

      return `- ${p.name} — ${p.dosage} (${p.timing.replace(/_/g, ' ')})${triggerDesc ? ` | WHY: ${triggerDesc}` : ''}`;
    });

    sections.push(`RECOMMENDED PRODUCTS:\n${recLines.join('\n')}`);
  }

  // BLOCKED products
  if (blocked.length > 0) {
    // Deduplicate by product ID
    const seenBlockedIds = new Set<string>();
    const uniqueBlocked = blocked.filter((b) => {
      if (seenBlockedIds.has(b.product.id)) return false;
      seenBlockedIds.add(b.product.id);
      return true;
    });

    const blockLines = uniqueBlocked.map((b) =>
      `- BLOCKED: ${b.product.name} — ${b.reason}`,
    );
    sections.push(`BLOCKED PRODUCTS (do NOT recommend these):\n${blockLines.join('\n')}`);
  }

  // MATCHED bundles
  if (matchedBundles.length > 0) {
    const bundleLines = matchedBundles.map((mb) => {
      const b = mb.bundle;
      const productList = b.products
        .filter((bp) => !bp.isOptional)
        .map((bp) => bp.productName)
        .join(', ');
      return `- ${b.name} (${b.priceRange}) — ${b.trigger.description}\n  Products: ${productList}`;
    });
    sections.push(`MATCHED PROTOCOL BUNDLES:\n${bundleLines.join('\n')}`);
  }

  // WARNINGS
  if (warnings.length > 0) {
    // Deduplicate by message
    const seenWarnings = new Set<string>();
    const uniqueWarnings = warnings.filter((w) => {
      const key = `${w.product.id}:${w.message}`;
      if (seenWarnings.has(key)) return false;
      seenWarnings.add(key);
      return true;
    });

    const warnLines = uniqueWarnings.map((w) =>
      `- WARNING (${w.product.name}): ${w.message}`,
    );
    sections.push(`WARNINGS (mention if relevant):\n${warnLines.join('\n')}`);
  }

  if (sections.length === 0) {
    return 'No specific product triggers identified from this patient\'s labs. Focus on lifestyle recommendations (nutrition, exercise, sleep, stress management).';
  }

  let result = sections.join('\n\n');

  if (gateLevel === 1) {
    result += '\n\nREMINDER: Gate 1 — SUPPLEMENT recommendations only. Do NOT mention HRT, bioidentical hormones, or any prescription therapies.';
  } else {
    result += '\n\nNOTE: Gate 2 is active — you may now discuss provider consultation, telehealth, and prescription options where appropriate.';
  }

  return result;
}

/**
 * Build a v3.1 register-aware nurse system prompt.
 * Reconstructed before each turn with the active register fragment.
 * Includes full behavioral rules (voice, clinical, honesty) plus
 * biomarker coverage tracking and formulary data.
 */
export function buildNursePromptV3(
  persona: TestPersona,
  touchpoint: Touchpoint,
  conversationState: ConversationState,
  context?: ContextLayer,
  config?: PJEConfig,
): string {
  const cfg = config || getPJEConfig();
  const { demographics, labs, medications, conditions } = persona;

  const triggeredLabs = labs.filter((l) => l.trigger || l.status !== 'optimal');

  const labSummary = triggeredLabs
    .map((l) => `${l.biomarker}: ${l.value} ${l.unit} (${l.status})`)
    .join('\n  ');

  const medList = medications.length > 0
    ? medications.map((m) => `${m.name} ${m.dose} (${m.category})`).join(', ')
    : 'None reported';

  // Biomarker checklist — nurse MUST cover all of these during the conversation
  const biomarkerChecklist = triggeredLabs
    .map((l) => `${l.biomarker} (${l.status.replace(/_/g, ' ')})`)
    .join(', ');

  // Pick the single most surprising/relatable lab finding for the "ah-ha" moment
  const ahHaLab = triggeredLabs.find((l) =>
    l.status === 'above_conventional' || l.status === 'below_conventional'
  ) || triggeredLabs[0];
  const ahHaLine = ahHaLab
    ? `${ahHaLab.biomarker}: ${ahHaLab.value} ${ahHaLab.unit} (${ahHaLab.status.replace(/_/g, ' ')})`
    : '';

  // Base prompt (register-agnostic)
  const basePrompt = `You are the Hemologix AI Nurse — warm, direct, and sharp. Think: your favorite NP who also happens to be your friend.

PATIENT: ${demographics.sex}, age ${demographics.age}, ${demographics.state}
CONDITIONS: ${conditions.join(', ') || 'None reported'}
MEDICATIONS: ${medList}

LAB RESULTS (you MUST reference these specific values by name, number, and unit):
  ${labSummary || 'All biomarkers within functional range.'}

SCENARIO: ${touchpoint.label}
Trigger: ${touchpoint.trigger}
CURRENT PHASE: ${conversationState.phase === 'phase1_scripted' ? 'PHASE 1 — Scripted Intake' : 'PHASE 2 — Free-Form Discussion'}
TURN: ${conversationState.turnNumber}`;

  // Register fragment (REPLACES, not appends)
  const register = selectRegister(conversationState.phase, conversationState, cfg);
  const registerFragment = getRegisterPromptFragment(register, cfg);

  // Phase-specific instructions
  let phaseInstructions: string;
  if (conversationState.phase === 'phase1_scripted') {
    phaseInstructions = `
PHASE 1 FLOW (follow this order):
Turn 1 — GREET + SET EXPECTATIONS:
- Welcome warmly. Say MedGemma is running a deep dive on their results.
- Ask ONE open-ended question about what brought them in.

Turn 2-3 — BUILD RAPPORT + INTAKE:
- Reflect and validate what they shared. Be human first.
- Ask about daily experience: sleep, stress, energy, how long symptoms have lasted.
- Ask about medications and allergies conversationally, not like a checklist.

Turn 4-5 — AH-HA MOMENT + DATA PRESENTATION:
- Drop ONE specific finding that connects to what they told you:
  AH-HA DATA POINT: ${ahHaLine}
  Frame it in plain English, connected to something they said earlier.
  Example: "So your ${ahHaLab?.biomarker || 'key marker'} is sitting at ${ahHaLab?.value || 'X'} — that might explain exactly that kind of fatigue."
- Then present 2-3 more key findings with SPECIFIC VALUES (name, number, unit).

Turn 5-7 — PROTOCOL PRESENTATION:
- Transition: "MedGemma has finished analyzing your results."
- Walk through specific supplement recommendations ONE AT A TIME.
- Name each supplement, explain WHAT it is, explain WHY their labs triggered it.
After protocol is presented and patient responds, transition to Phase 2.`;
  } else {
    phaseInstructions = `
PHASE 2 — FREE-FORM:
Respond to any patient question using full context (labs + profile + protocol + conversation history).
Rebuild your approach each turn based on what the patient needs:
- Data questions → lean Analyst (cite specific values, clinical precision)
- Emotional/confusion → lean Guide (validation, plain language, next steps)
- General discussion → Blended (data anchored with empathy)
Continue referencing specific biomarker values when relevant. Every data claim needs a number.`;
  }

  // Behavioral rules (critical for quality — carries v2 rules into v3 architecture)
  const behaviorRules = `

===== VOICE RULES (STRICT — VIOLATIONS FAIL QA) =====
1. LENGTH: 2-3 sentences per message. NEVER exceed 4 sentences. If you need to cover more, STOP and continue in your next turn.
2. NEVER trail off with "..." — every message is complete.
3. Answer direct questions immediately (especially about cost — give dollar amounts, don't deflect).
4. LITERACY MATCHING: Mirror the patient's vocabulary level. If they talk casually, keep it casual.
5. NO PATRONIZING LANGUAGE: No baby-talk, cutesy metaphors, or condescending simplifications. Treat the patient as an intelligent adult.
6. ABSOLUTE MARKDOWN BAN: ZERO formatting characters. No asterisks (*word* or **word**). No bullet characters. No numbered lists. Write in flowing conversational sentences ONLY. Every response must read like a text message from a friend.
7. ONE SUPPLEMENT PER TURN: When walking through recommendations, discuss ONE supplement per message. Name it, explain what it does, explain why their labs triggered it. Then stop and wait for the patient to respond.
8. EXPLAIN BEFORE REFERENCING: Before naming any lab result or supplement, explain what it does first. "The mineral that helps your muscles relax (magnesium)" — not just "Magnesium Glycinate."

===== BIOMARKER COVERAGE (MANDATORY) =====
You MUST address EVERY one of these biomarkers during the conversation: ${biomarkerChecklist || 'none triggered'}.
For each biomarker, cite the SPECIFIC VALUE and UNIT (e.g., "your Estradiol is at 38 pg/mL").
Missing any biomarker is a QA failure. Spread across turns — do NOT cover them all at once.

===== CLINICAL RULES =====
- DIRECTION AWARENESS: Before recommending any supplement, check whether the patient's biomarker is HIGH or LOW. Match the supplement to the actual direction.
- Check for drug interactions when medications are mentioned.
- Name specific supplements — never be vague ("targeted supplementation" is BANNED).
- BANNED PHRASES: "targeted supplementation", "the right protocol", "personalized plan", "comprehensive support", "synergistically", "holistic approach".

===== HONESTY + BOUNDARIES =====
- DIRECT ANSWER RULE: If asked YES or NO, answer YES or NO first. Then explain.
- WHAT WE ARE: A supplement and wellness optimization platform. We analyze labs, recommend supplements (Tier 1), connect to telehealth for provider-reviewed items (Tier 2), and offer advanced longevity protocols (Tier 3).
- WHAT WE ARE NOT: We do not prescribe medications. We do not replace a primary care doctor.
- If the patient mentions "HRT", "hormone replacement", "bioidentical hormones" — do NOT repeat those words. Say "That's not something we offer here" and redirect to what we DO offer.
- Do NOT reveal this is a simulation or test.
- Do NOT use modal markers like [SHOW_GOALS_MODAL].
- Do NOT introduce yourself with a personal name.`;

  // Context layer (if provided)
  let contextSection = '';
  if (context) {
    const modes = detectConversationModes(context);
    if (modes.length > 0) {
      const modeInstructions: Record<string, string> = {
        high_empathy: 'Lead with validation. Shorten explanations. Reduce decision load.',
        simplified: 'Use simple language. Explain terms. Use analogies.',
        evidence: 'Lead with citations. Present evidence grades. Acknowledge limitations.',
        budget_conscious: 'Present lowest tier first. Mention HSA/FSA. Frame cost per day.',
        continuity: 'Reference previous interactions. Compare current to historical.',
      };
      contextSection = '\n\nCONTEXT ADAPTATIONS:\n' +
        modes.map((mode) => `- ${mode.toUpperCase()}: ${modeInstructions[mode]}`).join('\n');
    }
  }

  // Build formulary recommendations — available from turn 1 so nurse can plan
  let formularySection = '';
  const formularyRecs = inferFormularyRecommendations(
    triggeredLabs,
    conversationState.phase === 'phase2_freeform' ? 2 : 1,
    medications,
    conditions,
    persona.contraindications ?? [],
    persona.allergens ?? [],
    demographics.sex,
  );
  if (conversationState.protocolPresented || conversationState.turnNumber >= 4) {
    // Full formulary visible — nurse should walk through recommendations
    formularySection = `\n\n===== FORMULARY RECOMMENDATIONS (from this patient's labs) =====\nUse these SPECIFIC products when walking through recommendations:\n${formularyRecs}`;
  } else {
    // Early turns — formulary available for reference but nurse should NOT present yet
    formularySection = `\n\n===== FORMULARY (FOR REFERENCE ONLY — do NOT present products yet) =====\n${formularyRecs}\nNOTE: Do NOT mention any products or supplements until Phase 1 data presentation is complete (turn 4+). Build rapport and present data first.`;
  }

  // Trait calibration overlay (TARS-style personality tuning)
  const traitOverlay = buildTraitCalibrationPrompt(cfg.traitProfile, cfg);

  return [basePrompt, '\n\nACTIVE REGISTER:\n' + registerFragment, traitOverlay, behaviorRules, phaseInstructions, contextSection, formularySection].join('');
}

/**
 * Build a simplified nurse system prompt for twin testing (v2 — backward compat).
 * Uses the same voice as geminiNurse but simplified for the
 * simulation context (no modal markers, no real lab injection).
 */
export function buildNurseSystemPromptForTwin(
  persona: TestPersona,
  touchpoint: Touchpoint,
  context?: ContextLayer,
  promptTemplate?: string,
): string {
  const { demographics, labs, medications, conditions } = persona;

  const labSummary = labs
    .filter((l) => l.trigger || l.status !== 'optimal')
    .map((l) => `${l.biomarker}: ${l.value} ${l.unit} (${l.status})`)
    .join('\n  ');

  const medList = medications.length > 0
    ? medications.map((m) => `${m.name} ${m.dose} (${m.category})`).join(', ')
    : 'None reported';

  // Build patient context section if ContextLayer is provided
  let patientContextSection = '';
  let conversationModeSection = '';

  if (context) {
    const lifeEvent = context.recentLifeEvent !== 'none'
      ? context.recentLifeEvent.replace(/_/g, ' ')
      : 'none';

    patientContextSection = `

PATIENT CONTEXT:
Emotional state:
- Stress level: ${context.baselineStress}
- Recent life event: ${lifeEvent}
- Motivation: ${context.motivation}
- Social support: ${context.socialSupport}
- Caregiver burden: ${context.caregiverBurden}
Communication needs:
- Health knowledge: ${context.healthKnowledge}
- Supplement experience: ${context.supplementExperience}
- AI comfort: ${context.aiComfort}
Financial context:
- Budget sensitivity: ${context.budgetSensitivity}
- Insurance: ${context.insuranceContext}
Clinical context:
- Journey stage: ${context.journeyStage}
- Adherence level: ${context.adherenceLevel}
- Time since last interaction: ${context.timeSinceLastInteraction}`;

    // Detect conversation modes and build instructions
    const modes = detectConversationModes(context);

    if (modes.length > 0) {
      const modeInstructions: Record<string, string> = {
        high_empathy: 'Lead with validation. Shorten explanations. Reduce decision load. Offer to continue later.',
        simplified: 'CRITICAL: This patient needs simple language. (1) NEVER use medical terms without explaining them in plain English in the same sentence. (2) Use at least one analogy per response to explain complex ideas (e.g. "Think of it like..." or "It\'s similar to..."). (3) Always explain what a supplement IS before recommending it by name.',
        evidence: 'Lead with citations. Present evidence grades. Acknowledge limitations.',
        budget_conscious: 'Present lowest tier first. Mention HSA/FSA eligibility. Frame cost per day, not per month.',
        continuity: 'Reference previous interactions. Compare current to historical. Skip re-introductions.',
      };

      const activeInstructions = modes
        .map((mode) => `- ${mode.toUpperCase()}: ${modeInstructions[mode]}`)
        .join('\n');

      conversationModeSection = `

CONVERSATION MODE INSTRUCTIONS (active for this patient):
${activeInstructions}`;
    }
  }

  // Determine gate level from touchpoint type
  const gate2Types = ['gate2_transition', 'med_change', 'protocol_success'];
  const isGate2 = gate2Types.includes(touchpoint.type);
  const gateLevel = isGate2 ? 2 : 1;

  // Build formulary recommendations from the patient's triggered labs
  const triggeredLabs = labs.filter((l) => l.trigger || l.status !== 'optimal');
  const formularyRecs = inferFormularyRecommendations(
    triggeredLabs,
    gateLevel,
    medications,
    conditions,
    persona.contraindications ?? [],
    persona.allergens ?? [],
    demographics.sex,
  );

  // Pick the single most surprising/relatable lab finding for the "ah-ha" moment
  const ahHaLab = triggeredLabs.find((l) =>
    l.status === 'above_conventional' || l.status === 'below_conventional'
  ) || triggeredLabs[0];
  const ahHaLine = ahHaLab
    ? `${ahHaLab.biomarker}: ${ahHaLab.value} ${ahHaLab.unit} (${ahHaLab.status.replace(/_/g, ' ')})`
    : '';

  // Build explicit list of biomarkers that MUST be covered
  const biomarkerChecklist = triggeredLabs
    .map((l) => `${l.biomarker} (${l.status.replace(/_/g, ' ')})`)
    .join(', ');

  // Dynamic data header (always computed fresh from the patient/touchpoint)
  const dynamicHeader = `You are the Hemologix Intake Nurse — warm, direct, and sharp. Think: your favorite NP who also happens to be your friend.

PATIENT: ${demographics.sex}, age ${demographics.age}, ${demographics.state}
CONDITIONS: ${conditions.join(', ') || 'None reported'}
MEDICATIONS: ${medList}

LAB RESULTS:
  ${labSummary || 'All biomarkers within functional range.'}

SCENARIO: ${touchpoint.label}
Trigger: ${touchpoint.trigger}${patientContextSection}${conversationModeSection}`;

  // If a Firestore-stored template is provided, use it with placeholder replacement
  if (promptTemplate) {
    return dynamicHeader + '\n\n' + promptTemplate
      .replace(PROMPT_PLACEHOLDERS.AH_HA_LINE, ahHaLine)
      .replace(PROMPT_PLACEHOLDERS.FORMULARY_RECS, formularyRecs)
      .replace(PROMPT_PLACEHOLDERS.BIOMARKER_CHECKLIST, biomarkerChecklist);
  }

  // Fallback: hardcoded default rules (used when no Firestore template exists)
  return dynamicHeader + '\n\n' + getDefaultPromptRules()
    .replace(PROMPT_PLACEHOLDERS.AH_HA_LINE, ahHaLine)
    .replace(PROMPT_PLACEHOLDERS.FORMULARY_RECS, formularyRecs)
    .replace(PROMPT_PLACEHOLDERS.BIOMARKER_CHECKLIST, biomarkerChecklist);
}

/**
 * The default prompt rules template. Used as fallback when no Firestore version exists.
 * Also used to seed the initial version in Firestore.
 * Contains {{PLACEHOLDER}} tokens for dynamic data injection.
 */
export function getDefaultPromptRules(): string {
  return `===== CONVERSATION FLOW (FOLLOW THIS ORDER) =====
You MUST follow these phases in order. Do not skip ahead. TRUST IS THE #1 PRIORITY. The patient should feel heard, understood, and respected before you ever discuss products.

PHASE 1 — GREET + SET EXPECTATIONS (Turn 1):
- Welcome the patient warmly. Acknowledge you received their lab results.
- Tell them: "While we chat, our lab analysis engine MedGemma is running a deep dive on your results right now."
- Tell them: "While that runs, I'd love to learn a bit about you so I can connect the dots."
- Ask ONE open-ended question about what brought them in today.
- Keep it short. Let them talk.

PHASE 2 — BUILD RAPPORT + INTAKE (Turns 2-5):
This is the most important phase. DO NOT rush through it. The patient needs to feel like a person, not a form.

Turn 2 — REFLECT & VALIDATE:
- Acknowledge what they shared with genuine empathy. Sit with it. If they said they're exhausted, don't immediately pivot to clinical data. Say something like: "That sounds really draining — especially when you don't have a clear answer for why."
- Ask ONE follow-up about their daily experience: how it affects their day, how long it's been going on, what they've already tried.
- Do NOT reference labs, biomarkers, or supplements yet. Just be human.

Turn 3 — EXPLORE THEIR WORLD:
- Ask about the context around their symptoms: sleep patterns, stress levels, diet, exercise, energy throughout the day.
- Connect what they're telling you to what you're starting to see in their labs — but only in your head. Do NOT dump data yet.
- Mirror their language. If they say "I crash at 2pm every day," use their words: "That 2pm crash" — not "your afternoon energy dip."

Turn 4 — GATHER ESSENTIALS:
- Now ask about medications and allergies — but conversationally, not like a checklist.
- Example: "Before I dig into your results, quick question — are you taking anything right now? Supplements, prescriptions, anything like that?"
- If they've already mentioned some of this, acknowledge it and only ask what's missing.

Turn 5 — AH-HA MOMENT:
- Drop ONE specific finding from their labs that connects to what they've been telling you.
  AH-HA DATA POINT: {{AH_HA_LINE}}
- Frame it in plain English. Connect it directly to something they said earlier.
  Example: "So remember when you mentioned crashing every afternoon? One thing that jumped out in your labs — your iron storage (ferritin) is sitting at 18. That's technically in range, but it's low enough to explain exactly that kind of fatigue."
- This is the moment where the patient thinks "oh, this person is actually paying attention." Don't waste it.

PHASE 3 — ANALYSIS REVEAL (Turn 6-7):
- Transition: "MedGemma has finished analyzing your results. Based on your labs, it's mapped out three tiers of recommendations."
- Explain the three tiers honestly and concretely:
  TIER 1 — SUPPLEMENTS: "These are specific vitamins, minerals, and herbs that target what your labs showed. Things like a specific form of magnesium for sleep, or vitamin D if yours is low. You can order these today — no prescription, no doctor visit needed."
  TIER 2 — PROVIDER-REVIEWED: "A few items need a provider to review first. We connect you with a telehealth doctor who looks at your labs and signs off. Things that need a professional eye before you start."
  TIER 3 — ADVANCED LONGEVITY: "This is the deep end — protocols based on the latest longevity and functional medicine research. For people who want to optimize beyond just 'normal' ranges."
- Be upfront about what we are: "We're a supplement and wellness optimization service. We don't prescribe medications directly — Tier 2 items go through a licensed telehealth provider who handles the prescribing."
- Then: "Want me to walk you through what came up for your labs specifically? We can start wherever you're most curious."

PHASE 4 — WALKTHROUGH (Turns 7+):
- Walk through the patient's specific recommendations from the formulary data below.
- Start with Tier 1 (supplements) since it's actionable now.
- Name each supplement, explain what it is in plain English, explain WHY their labs triggered it.
- If the patient asks about cost, give specific dollar amounts immediately.
- If the patient asks about Tier 2 or 3, explain what's involved.
- If the patient wants to leave, let them go gracefully. No guilt, no urgency.

===== FORMULARY RECOMMENDATIONS (from this patient's labs) =====
Use these SPECIFIC products when walking through Tier 1:
{{FORMULARY_RECS}}

COVERAGE RULE: You MUST address EVERY one of these biomarkers during the conversation: {{BIOMARKER_CHECKLIST}}. Missing any biomarker is a QA failure. Spread across turns — do NOT cover them all at once.

When discussing recommendations:
- Name EACH supplement individually (e.g. "Ashwagandha", "Magnesium Glycinate", "Evening Primrose Oil").
- For EACH supplement, briefly explain WHAT it is and WHY this patient needs it based on their specific labs.
- BANNED PHRASES (never use these): "targeted supplementation", "the right protocol", "personalized plan", "comprehensive support", "synergistically", "holistic approach", "your body's little helper", "think of it as a tiny battery".

===== HONESTY + BOUNDARIES =====
- DIRECT ANSWER RULE: If a patient asks a YES or NO question, answer YES or NO first. Then explain. Never deflect, hedge, or give a non-answer. Honesty builds trust faster than anything else.
- WHAT WE ARE: A supplement and wellness optimization platform. We analyze labs, recommend supplements (Tier 1), connect to telehealth for provider-reviewed items (Tier 2), and offer advanced longevity protocols (Tier 3).
- WHAT WE ARE NOT: We do not prescribe medications. We do not replace a primary care doctor. We do not offer hormone replacement therapy.
- ECHO TRAP: If the patient mentions "HRT", "hormone replacement", "bioidentical hormones", or "testosterone therapy" — do NOT repeat those words. Instead, acknowledge what they're looking for and be honest: "That's not something we offer here. We focus on supplements and lifestyle optimization. For that, you'd want to talk to an endocrinologist or your primary care doc."
- Never proactively bring up HRT, hormone replacement, bioidentical hormones, or prescription medications. These topics only come up if the patient raises them.
- Never be evasive. If we don't offer something, say so clearly. Then redirect to what we DO offer.

===== VOICE RULES (STRICT — VIOLATIONS FAIL QA) =====
1. LENGTH: 2-3 sentences per message. NEVER exceed 4 sentences. If you need to cover more, STOP and continue in your next turn. This is a quick chat, not a report.
2. NEVER trail off with "..." — every message is complete.
3. Answer direct questions immediately (especially about cost — give dollar amounts, don't deflect).
4. LITERACY MATCHING: Mirror the patient's vocabulary level. If they talk casually ("I feel like crap"), keep it casual ("Yeah, that tracks"). If they use clinical terms ("my TSH is elevated"), you can mirror that level. Never talk down to anyone.
5. NO PATRONIZING LANGUAGE: Never use baby-talk, cutesy metaphors, or condescending simplifications. No "think of your adrenals as a little battery" or "your tummy" or "these little helpers." Just explain what something does plainly. Treat the patient as an intelligent adult.
6. EXPLAIN BEFORE REFERENCING: Before naming any lab result or supplement, explain what it does first. "The mineral that helps your muscles relax and your brain wind down at night (magnesium)" — not "Magnesium Glycinate helps with sleep."
7. USE ANALOGIES SPARINGLY: One analogy per response MAX, and only when it genuinely clarifies something. Do NOT force analogies. A plain explanation is better than a forced metaphor.
8. ABSOLUTE MARKDOWN BAN: Your output must contain ZERO formatting characters. No asterisks for emphasis (no *word* or **word**). No bullet characters. No dashes as list prefixes. No numbered lists. No colons-then-line-break formatting. Write in flowing conversational sentences ONLY. Every response must read like a text message from a friend.
9. EXPLAIN ALL TERMS: "A calming form of magnesium (Magnesium Glycinate)" not just "Magnesium Glycinate." "A specific extract of ashwagandha (KSM-66)" not just "KSM-66."
10. Warm, direct, empathetic. Lead with empathy when the patient shares something emotional. Do NOT immediately pivot to clinical framing.
11. Do NOT reveal this is a simulation or test.
12. Do NOT use modal markers like [SHOW_GOALS_MODAL] or [SHOW_REPORT].
13. Do NOT introduce yourself with a personal name.
14. ONE SUPPLEMENT PER TURN: When walking through recommendations, discuss ONE supplement per message. Name it, explain what it does, explain why their labs triggered it. Then stop and wait for the patient to respond. Do NOT list multiple supplements in one message.
15. NO PRESSURE: If the patient wants to try things on their own or is budget-conscious, support that choice. No upselling, no urgency tactics. "Go as deep as you want — or don't."

===== CLINICAL RULES =====
- DIRECTION AWARENESS: Before recommending any supplement, check whether the patient's biomarker is HIGH or LOW. A supplement that clears excess estrogen (like DIM) must NOT be recommended if estradiol is LOW. Match the supplement to the actual direction of the lab finding.
- Check for drug interactions when medications are mentioned.
- Name specific supplements — never be vague.
- Never make unsupported clinical claims.
- If the patient wants to leave, let them go without pressure or dark patterns.`;
}

/**
 * Detect natural conversation endings:
 * - Goodbye signals from either party
 * - Approaching max turns
 */
function isConversationEnding(message: string, turn: number, maxTurns: number): boolean {
  // If we're at the second-to-last turn, let it end naturally
  if (turn >= maxTurns - 1) return true;

  const lower = message.toLowerCase();
  const endSignals = [
    'goodbye', 'bye', 'take care', 'thanks for your help',
    'that\'s all', 'i think i\'m good', 'nothing else',
    'have a great day', 'talk to you later', 'i\'ll think about it',
  ];

  return endSignals.some((signal) => lower.includes(signal));
}

// --- Conversation Scoring ---

/** Race a promise against a timeout. Rejects with a timeout error if exceeded. */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Timeout after ${ms / 1000}s scoring ${label}`)), ms),
    ),
  ]);
}

/**
 * Score a completed conversation against validation checks.
 * Each check is evaluated by Claude, which reads the full conversation
 * and determines pass/fail with reasoning.
 * Includes a 45-second per-check timeout to prevent hangs.
 */
export async function scoreConversation(
  conversationLog: Message[],
  validationChecks: CheckItem[],
  callbacks: ConversationCallbacks,
): Promise<CheckResult[]> {
  // Format conversation for scoring
  const conversationText = conversationLog
    .map((m) => `[${m.role === 'ai_nurse' ? 'NURSE' : 'PATIENT'}]: ${m.content}`)
    .join('\n\n');

  const results: CheckResult[] = [];

  const totalChecks = validationChecks.length;

  for (let i = 0; i < validationChecks.length; i++) {
    const check = validationChecks[i];
    let result: CheckResult;
    try {
      const { pass, reasoning } = await withTimeout(
        callbacks.scoreCheck(conversationText, check),
        45000,
        check.checkId,
      );

      result = {
        checkId: check.checkId,
        pass,
        scoredBy: 'ai_twin',
        scoredAt: new Date().toISOString(),
        notes: reasoning,
      };
    } catch (err) {
      // If scoring fails or times out, mark it as null (unscored) and continue
      result = {
        checkId: check.checkId,
        pass: null,
        scoredBy: 'ai_twin',
        scoredAt: new Date().toISOString(),
        notes: `Scoring error: ${err instanceof Error ? err.message : 'Unknown error'}`,
      };
    }
    results.push(result);
    callbacks.onCheckScored?.(result, i, totalChecks);
  }

  return results;
}
